/**
 * Build a preview of the delivery EMAIL for scheduled automations that deliver
 * by email ('email' / 's3+email'). The sibling of report-doc.ts for the
 * email-template builder.
 *
 * The shell mirrors the backend delivery template's email rendering
 * (wg-agents-studio-api .../renderer/_templates_async.py: EMAIL_SHELL /
 * _render_email / _email_body) so the previewed email looks like the one a
 * scheduled run actually sends. The body is rendered from the creator's
 * Markdown template (marked + DOMPurify, same as MarkdownPipe) after the
 * run-time placeholders are filled with representative sample content.
 */

import { Marked } from 'marked';
import DOMPurify from 'dompurify';
import { WizardEmailConfig } from '../services/agent-builder.interface';
import { resolveEmailCss } from './email-themes';
import { WG_LOGO_PNG_B64 } from './report-themes';

const emailMarked = new Marked();

function sanitizeHtml(raw: string): string {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  let dp: any = DOMPurify;
  if (typeof dp?.sanitize !== 'function' && typeof dp === 'function') {
    dp = dp(typeof window !== 'undefined' ? window : globalThis);
  }
  if (typeof dp?.sanitize !== 'function') return raw;
  return dp.sanitize(raw, { USE_PROFILES: { html: true } });
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

function escapeHtml(s: string): string {
  return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Run-time values substituted into an email body template. All optional — an
 *  absent value drops its placeholder line cleanly rather than leaving a stray
 *  "{{report_url}}". MUST match the backend's _fill_email_placeholders. */
export interface EmailPlaceholderValues {
  report?: string;
  summary?: string;
  report_url?: string;
  agent_name?: string;
  date?: string;
}

/** Default email body Markdown when meta.email.markdown is empty. Kept in sync
 *  with the Python DEFAULT_EMAIL_BODY_* in renderer/_templates_async.py.
 *   - email-only: the full report IS the message.
 *   - s3+email: a short summary + a button to the durable report link. */
export function defaultEmailBodyMarkdown(kind: string | undefined): string {
  if ((kind || '').toLowerCase() === 's3+email') {
    return '{{summary}}\n\n[View the full report]({{report_url}})';
  }
  return '{{report}}';
}

/** Fill {{placeholder}} tokens in an email body template. A token whose value is
 *  empty is removed; a Markdown link whose entire URL is an empty placeholder is
 *  dropped as a line so no broken "[text]()" survives. MUST match the backend's
 *  _fill_email_placeholders (renderer/_templates_async.py). */
export function fillEmailPlaceholders(md: string, values: EmailPlaceholderValues): string {
  const v: Record<string, string> = {
    report: values.report ?? '',
    summary: values.summary ?? '',
    report_url: values.report_url ?? '',
    agent_name: values.agent_name ?? '',
    date: values.date ?? '',
  };
  let out = md || '';
  // Drop a whole Markdown-link line when its URL is an empty placeholder
  // (e.g. the "View the full report" button for email-only, which has no link).
  out = out.replace(/^.*\]\(\{\{(report_url)\}\}\).*$/gm, (line) =>
    v['report_url'].trim() ? line : '',
  );
  out = out.replace(/\{\{(report|summary|report_url|agent_name|date)\}\}/g, (_m, k: string) => v[k] ?? '');
  // Collapse the blank lines a dropped placeholder can leave behind.
  return out.replace(/\n{3,}/g, '\n\n').trim();
}

/** Render an email body Markdown string to a sanitized HTML fragment. */
export function renderEmailBody(bodyMarkdown: string): string {
  const raw = emailMarked.parse(bodyMarkdown || '', { async: false }) as string;
  return sanitizeHtml(raw);
}

const EMAIL_SHELL = (
  subject: string,
  bodyHtml: string,
  css: string,
  hideBranding?: boolean,
  agentName?: string,
): string => {
  const head = hideBranding
    ? ''
    : `<div class="eml-head"><img class="eml-logo" src="data:image/png;base64,${WG_LOGO_PNG_B64}" alt="WatchGuard" /><span class="eml-brand-sub">AI Agents</span></div>`;
  const foot = hideBranding
    ? ''
    : `<div class="eml-foot">Delivered by WatchGuard AI Agents · This automated message was sent by the “${escapeHtml(agentName || 'Scheduled agent')}” agent.</div>`;
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(subject)}</title>
<style>${css}</style>
</head>
<body>
<div class="eml-wrap">
<div class="eml-card">
${head}
<div class="eml-body">${bodyHtml}</div>
${foot}
</div>
</div>
</body>
</html>`;
};

/** Representative sample content for the preview — stands in for what a live run
 *  produces, so the creator sees a realistic email. */
const SAMPLE_REPORT = `## Summary

This week's scheduled run completed successfully. Below are the highlights and
key metrics captured during the reporting window.

## Key findings

- **Throughput** held steady at 1,240 events/day (+4% week over week).
- Two anomalies were flagged and auto-resolved.
- No SLA breaches during the period.

| Metric | This week | Last week |
| --- | --- | --- |
| Processed | 8,680 | 8,340 |
| Errors | 12 | 19 |
| Uptime | 99.98% | 99.95% |

## Details

Full breakdown and per-source tables are included below. Reach out if you'd like
a deeper cut on any segment.`;

const SAMPLE_SUMMARY =
  'Your scheduled report for this week is ready. Throughput is up 4% and there were no SLA breaches. The full report is attached below.';

/** Build the preview HTML document for an email template. `kind` picks the
 *  default body + which sample placeholders are filled (email-only shows the
 *  full report inline; s3+email shows a summary + a link button). */
export function buildEmailPreviewHtml(
  config: WizardEmailConfig,
  agentName: string,
  kind: string | undefined,
): string {
  const isS3 = (kind || '').toLowerCase() === 's3+email';
  const subject = (config.subject || `${agentName || 'Scheduled agent'} report`).trim();
  const date = new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });
  const bodyTemplate = (config.markdown || '').trim() || defaultEmailBodyMarkdown(kind);
  const filled = fillEmailPlaceholders(bodyTemplate, {
    report: SAMPLE_REPORT,
    summary: SAMPLE_SUMMARY,
    report_url: isS3 ? 'https://reports.example.com/r/preview-link' : '',
    agent_name: agentName || 'Scheduled agent',
    date,
  });
  return EMAIL_SHELL(subject, renderEmailBody(filled), resolveEmailCss(config.css), config.hideBranding, agentName);
}

/** Build the themed email document for a REAL rendered body — a per-recipient
 *  dry run's email_previews entry. The body stands in for the run's report, so
 *  the creator's email template (or the default) wraps it exactly as a
 *  scheduled run would. */
export function buildEmailDeliveryHtml(
  config: WizardEmailConfig,
  agentName: string,
  kind: string | undefined,
  bodyMarkdown: string,
): string {
  const subject = (config.subject || `${agentName || 'Scheduled agent'} report`).trim();
  const date = new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });
  const bodyTemplate = (config.markdown || '').trim() || defaultEmailBodyMarkdown(kind);
  const filled = fillEmailPlaceholders(bodyTemplate, {
    report: bodyMarkdown,
    summary: bodyMarkdown,
    report_url: '',
    agent_name: agentName || 'Scheduled agent',
    date,
  });
  return EMAIL_SHELL(subject, renderEmailBody(filled), resolveEmailCss(config.css), config.hideBranding, agentName);
}
